import { useMemo, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import iso from "iso-3166-1";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import worldData from "world-atlas/countries-110m.json";

interface WorldMapProps {
  data: Map<string, number>;
}

interface Tooltip {
  x: number;
  y: number;
  label: string;
  count: number;
}

interface Position {
  coordinates: [number, number];
  zoom: number;
}

const DEFAULT_POSITION: Position = {
  coordinates: [15, 54],
  zoom: 2.5,
};

const ALPHA2_TO_NUMERIC: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const c of iso.all()) {
    m.set(c.alpha2, c.numeric);
  }
  return m;
})();

const NUMERIC_TO_NAME: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const c of iso.all()) {
    m.set(c.numeric, c.country);
  }
  return m;
})();

export function WorldMap({ data }: WorldMapProps) {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [position, setPosition] = useState<Position>(DEFAULT_POSITION);

  const numericData = useMemo(() => {
    const m = new Map<string, number>();
    let max = 0;
    for (const [alpha2, count] of data.entries()) {
      const numeric = ALPHA2_TO_NUMERIC.get(alpha2.toUpperCase());
      if (!numeric) continue;
      m.set(numeric, count);
      if (count > max) max = count;
    }
    return { byNumeric: m, max };
  }, [data]);

  const colorFor = (count: number): string => {
    if (count === 0) return "hsl(var(--secondary))";
    const intensity = numericData.max > 0 ? count / numericData.max : 0;
    if (intensity > 0.66) return "hsl(var(--nature-deep))";
    if (intensity > 0.33) return "hsl(var(--nature-mid))";
    return "hsl(var(--nature-light))";
  };

  const setZoom = (next: number) =>
    setPosition((p) => ({ ...p, zoom: Math.max(1, Math.min(8, next)) }));

  const isAtDefault =
    position.zoom === DEFAULT_POSITION.zoom &&
    position.coordinates[0] === DEFAULT_POSITION.coordinates[0] &&
    position.coordinates[1] === DEFAULT_POSITION.coordinates[1];

  return (
    <div className="relative" onMouseLeave={() => setTooltip(null)}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 130 }}
        style={{ width: "100%", height: "auto" }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          minZoom={1}
          maxZoom={8}
          onMoveEnd={(p) =>
            setPosition({ coordinates: p.coordinates as [number, number], zoom: p.zoom })
          }
        >
          <Geographies geography={worldData}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const numericId = String(geo.id).padStart(3, "0");
                const count = numericData.byNumeric.get(numericId) ?? 0;
                const name =
                  (geo.properties as { name?: string }).name ??
                  NUMERIC_TO_NAME.get(numericId) ??
                  "Ukjent";
                const hasData = count > 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={colorFor(count)}
                    stroke={hasData ? "hsl(var(--nature-deep))" : "hsl(var(--border))"}
                    strokeWidth={hasData ? 0.6 / position.zoom : 0.4 / position.zoom}
                    onMouseMove={(e) => {
                      const rect = (
                        e.currentTarget.ownerSVGElement?.parentElement as HTMLElement | null
                      )?.getBoundingClientRect();
                      setTooltip({
                        x: e.clientX - (rect?.left ?? 0),
                        y: e.clientY - (rect?.top ?? 0),
                        label: name,
                        count,
                      });
                    }}
                    style={{
                      default: { outline: "none" },
                      hover: {
                        outline: "none",
                        fill: hasData ? "hsl(var(--nature-deep))" : "hsl(var(--muted))",
                        cursor: hasData ? "pointer" : "default",
                      },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      <div className="absolute right-2 top-2 flex flex-col gap-1">
        <button
          type="button"
          onClick={() => setZoom(position.zoom * 1.5)}
          className="flex h-7 w-7 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-sm hover:bg-secondary hover:text-foreground"
          aria-label="Zoom inn"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setZoom(position.zoom / 1.5)}
          className="flex h-7 w-7 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-sm hover:bg-secondary hover:text-foreground"
          aria-label="Zoom ut"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        {!isAtDefault ? (
          <button
            type="button"
            onClick={() => setPosition(DEFAULT_POSITION)}
            className="flex h-7 w-7 items-center justify-center rounded-md border bg-card text-muted-foreground shadow-sm hover:bg-secondary hover:text-foreground"
            aria-label="Tilbakestill"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {tooltip ? (
        <div
          className="pointer-events-none absolute rounded-md border bg-card px-2.5 py-1.5 text-xs shadow-md"
          style={{
            left: tooltip.x + 12,
            top: tooltip.y + 12,
          }}
        >
          <div className="font-medium">{tooltip.label}</div>
          <div className="text-muted-foreground">
            {tooltip.count.toLocaleString("nb-NO")} besøk
          </div>
        </div>
      ) : null}
    </div>
  );
}
